import React, { useEffect, useMemo, useState } from 'react'
import styles from './accountProfileStrength.module.scss'
import useWindowSize from '../../hooks/use-windowSize'
import { POST_SCORE_HREF, PSQ_HREF, TABLET_BREAKPOINT } from 'constants/global'
import {
  calculateCompletion,
  hasAvatar,
  hasPublishedMatches,
  hasResidence,
  hasWtn,
  playerInfoComplete,
} from 'utils'
import Accordion from 'components/generic/accordion'
import cx from 'classnames'
import { Box, LinearProgress } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import ProfileStrengthListItem from './ProfileStrengthListItem'
import { ReactComponent as CloseIcon } from 'images/icons/icn_close.svg'
import { getShouldHideProfileStrength } from 'utils/local-storage'
import { selectOwnProfile, selectOwnProfileLoading } from 'redux/selectors'
import { getOwnProfile } from 'actions/profile-actions'
import { useTranslation } from 'react-i18next'
import { usePrevious } from 'hooks/use-previous'
import { MyAccountSections } from 'constants/my-account'
import { useIsProfileStrengthHidden } from './hooks/useIsProfileStrengthHidden'
import { MIN_AGE_FOR_PSQ } from 'constants/profile'

interface Props {
  enableSelfSubmission?: boolean
  setActiveSection: (section: string) => void
}

const AccountProfileStrength = ({ enableSelfSubmission, setActiveSection }: Props) => {
  const { t } = useTranslation('strMyAccount')

  const windowWidth = useWindowSize().width
  const isMobile = windowWidth && windowWidth < TABLET_BREAKPOINT

  const dispatch = useDispatch()
  const accountDetails = useSelector((state: AppState) => state.user.accountDetails)
  const prevAccountDetails = usePrevious(accountDetails)

  const profile = useSelector(selectOwnProfile)

  const { isProfileStrengthHidden, hideProfileStrength } = useIsProfileStrengthHidden(
    profile?.tennisID
  )
  const completionPercentage = useMemo(() => calculateCompletion(profile), [profile])
  const isProfileComplete = completionPercentage === 100
  const [expanded, setExpanded] = useState(false)
  const isSelfSubmissionEnabled = enableSelfSubmission && profile?.eligibleForSelfSubmission

  const canSubmitPSQ = !!(profile?.age && profile?.age >= MIN_AGE_FOR_PSQ)

  const sectionScrollHeight = useMemo(
    () => ({
      avatar: isMobile ? 420 : 0,
      residence: 1000,
      playerInfo: isMobile ? 1750 : 1500,
    }),
    [isMobile]
  )

  const sections = useMemo(
    () => [
      {
        isCompleted: hasWtn(profile),
        copy: t('strProfileStrength.wtn'),
        isClickable: canSubmitPSQ,
        href: canSubmitPSQ ? PSQ_HREF : undefined,
      },
      {
        isCompleted: hasAvatar(profile),
        copy: t('strProfileStrength.avatar'),
        onClick: () => {
          setActiveSection(MyAccountSections.MY_DETAILS)
          window.scrollTo({
            top: sectionScrollHeight.avatar,
            behavior: 'smooth',
          })
        },
      },
      {
        isCompleted: hasResidence(profile),
        copy: t('strProfileStrength.residence'),
        onClick: () => {
          setActiveSection(MyAccountSections.MY_DETAILS)
          window.scrollTo({
            top: sectionScrollHeight.residence,
            behavior: 'smooth',
          })
        },
      },
      {
        isCompleted: playerInfoComplete(profile),
        copy: t('strProfileStrength.playerInfo'),
        onClick: () => {
          setActiveSection(MyAccountSections.MY_DETAILS)
          window.scrollTo({
            top: sectionScrollHeight.playerInfo,
            behavior: 'smooth',
          })
        },
      },
      {
        isCompleted: hasPublishedMatches(profile),
        copy: isSelfSubmissionEnabled
          ? t('strProfileStrength.postMatch')
          : t('strProfileStrength.haveResult'),
        isClickable: isSelfSubmissionEnabled,
        href: isSelfSubmissionEnabled ? POST_SCORE_HREF : undefined,
      },
    ],
    [t, profile, isSelfSubmissionEnabled, canSubmitPSQ, sectionScrollHeight, setActiveSection]
  )

  useEffect(() => {
    if (prevAccountDetails && prevAccountDetails !== accountDetails) {
      dispatch(getOwnProfile())
    }
  }, [dispatch, accountDetails, prevAccountDetails])

  if (
    (profile?.tennisID && getShouldHideProfileStrength(profile.tennisID)) ||
    isProfileStrengthHidden
  )
    return null

  return (
    <>
      {!isMobile ? (
        <div>
          <div className={styles.sideNav}>
            <div>
              {isProfileComplete && (
                <button
                  onClick={hideProfileStrength}
                  className={styles.closeBtn}
                  aria-label='close modal'
                >
                  <CloseIcon aria-hidden />
                </button>
              )}
            </div>
            <div
              className={
                isProfileComplete ? styles.sideNavInnerComplete : styles.sideNavInnerIncomplete
              }
            >
              <div className={styles.heading}>
                <span>{t('profileStrength')}</span>
              </div>
              <div className={styles.progressBarContainer}>
                <div className={styles.completionPercentage}>
                  {t('completionPercentage', { count: completionPercentage })}
                </div>
                <Box width='100%'>
                  <LinearProgress
                    variant={'determinate'}
                    value={completionPercentage}
                    classes={{
                      barColorPrimary: styles.progressBarColor,
                      colorPrimary: styles.progressBarSecondaryColor,
                      bar: styles.progressBar,
                    }}
                    className={styles.linearProgress}
                  />
                </Box>
              </div>
            </div>

            {!isProfileComplete && (
              <div className={styles.completionList}>
                <ul className={styles.list}>
                  {sections.map((item, index) => (
                    <ProfileStrengthListItem key={index} {...item} />
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className={styles.content}>
            <div className={styles.sideNavContainer}>
              <div className={styles.mobileNav}>
                {!isProfileComplete ? (
                  <div>
                    <Accordion className={cx(styles.accordion)} expanded={expanded}>
                      <Accordion.Summary
                        className={styles.summary}
                        onClick={() => {
                          setExpanded(!expanded)
                        }}
                      >
                        <div>
                          <div className={styles.mobileHeading}>
                            <span>{t('profileStrength')}</span>
                          </div>
                          <div>
                            <div className={styles.progressBarContainerMobile}>
                              <div className={styles.completionPercentageMobile}>
                                {t('completionPercentage', { count: completionPercentage })}
                              </div>
                              <Box width='100%'>
                                <LinearProgress
                                  variant={'determinate'}
                                  value={completionPercentage}
                                  classes={{
                                    barColorPrimary: styles.progressBarColor,
                                    colorPrimary: styles.progressBarSecondaryColor,
                                    bar: styles.progressBar,
                                  }}
                                  className={styles.linearProgress}
                                />
                              </Box>
                            </div>
                          </div>
                        </div>
                      </Accordion.Summary>
                      <Accordion.Details className={styles.content}>
                        <div className={styles.completionListMobile}>
                          <ul className={styles.list}>
                            {sections.map((item, index) => (
                              <ProfileStrengthListItem key={index} {...item} />
                            ))}
                          </ul>
                        </div>
                      </Accordion.Details>
                    </Accordion>
                  </div>
                ) : (
                  <div className={styles.mobileCompletedDisplay}>
                    <div>
                      {isProfileComplete && (
                        <div>
                          <button
                            onClick={hideProfileStrength}
                            className={styles.closeBtn}
                            aria-label='close modal'
                          >
                            <CloseIcon aria-hidden />
                          </button>
                        </div>
                      )}

                      <div className={styles.mobileHeading}>
                        <span>{t('profileStrength')}</span>
                      </div>
                      <div>
                        <div className={styles.progressBarContainerMobile}>
                          <div className={styles.completionPercentageMobile}>
                            {t('completionPercentage', { count: completionPercentage })}
                          </div>
                          <Box width='100%'>
                            <LinearProgress
                              variant={'determinate'}
                              value={completionPercentage}
                              classes={{
                                barColorPrimary: styles.progressBarColor,
                                colorPrimary: styles.progressBarSecondaryColor,
                                bar: styles.progressBar,
                              }}
                              className={styles.linearProgress}
                            />
                          </Box>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default AccountProfileStrength
